import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getExternalAnalysisStatus,
  computeAnalysisProgress,
  ExternalAnalysisStatusPayload,
  ExternalAnalysisStatusValue,
} from '../api/externalAnalysis';

interface ExternalAnalysisStatusProps {
  projectId: string;
  taskId: string;
  onCompleted: () => void;
  onFailed: (reason: string) => void;
}

const STATUS_LABEL_KEYS: Record<ExternalAnalysisStatusValue, string> = {
  pending: 'externalAnalysis.statusPending',
  running: 'externalAnalysis.statusRunning',
  succeeded: 'externalAnalysis.statusSucceeded',
  failed: 'externalAnalysis.statusFailed',
  cancelled: 'externalAnalysis.statusCancelled',
};

const POLL_INTERVAL_MS = 5000;

// Keep latest callback in a ref so the polling effect doesn't tear down on
// every parent re-render. Callers may pass inline arrow functions safely.
function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

const ExternalAnalysisStatus: React.FC<ExternalAnalysisStatusProps> = ({
  projectId,
  taskId,
  onCompleted,
  onFailed,
}) => {
  const { t } = useTranslation('common');
  const [payload, setPayload] = useState<ExternalAnalysisStatusPayload | null>(null);

  const onCompletedRef = useLatestRef(onCompleted);
  const onFailedRef = useLatestRef(onFailed);

  useEffect(() => {
    let cancelled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();

    const scheduleNext = (): void => {
      if (cancelled) return;
      timeoutHandle = setTimeout(poll, POLL_INTERVAL_MS);
    };

    const poll = async (): Promise<void> => {
      try {
        const next = await getExternalAnalysisStatus(projectId, taskId, controller.signal);
        if (cancelled) return;
        setPayload(next);
        if (next.status === 'succeeded') {
          onCompletedRef.current();
          return; // terminal: do not reschedule
        }
        if (next.status === 'failed' || next.status === 'cancelled') {
          // Prefer partner-shape `error` (API-drama.md §3.2) when present, then
          // fall back to a `failed_step` hint so users learn which stage broke,
          // and finally to the generic status label.
          const failureReason =
            next.error ||
            (next.failedStep ? t('externalAnalysis.stepFailed', { step: next.failedStep }) : undefined) ||
            t(STATUS_LABEL_KEYS[next.status]);
          onFailedRef.current(failureReason);
          return; // terminal: do not reschedule
        }
        scheduleNext();
      } catch (err) {
        if (cancelled) return;
        // Distinguish abort (intentional cancellation on unmount/taskId change)
        // from transient network blips. Aborts must not be treated as errors.
        const isAbort =
          (err instanceof DOMException && err.name === 'AbortError') ||
          (err instanceof Error && err.name === 'AbortError');
        if (isAbort) return;
        // Transient error: keep polling, log with component prefix + taskId.
        // eslint-disable-next-line no-console
        console.warn(
          `[ExternalAnalysisStatus] poll failed taskId=${taskId}, will auto-retry`,
          err,
        );
        scheduleNext();
      }
    };

    // Fire once immediately, then chain via setTimeout inside poll().
    poll();

    return () => {
      cancelled = true;
      if (timeoutHandle !== null) {
        clearTimeout(timeoutHandle);
      }
      controller.abort();
    };
  }, [projectId, taskId, t, onCompletedRef, onFailedRef]);

  const status = payload?.status ?? 'pending';
  const label = t(STATUS_LABEL_KEYS[status]);
  // Prefer the partner's fine-grained numeric progress (sub-tasks within a
  // stage) for a smoother bar, falling back to stage cardinality when the
  // partner hasn't published it yet. Falls back to 0% before the first poll.
  const progress = payload ? computeAnalysisProgress(payload) : 0;
  const showProgress = status === 'running' || status === 'pending';
  // 0% 表示外部平台还没真正开始跑（仍在队列里）：显示排队提示，不显示「0%」。
  const isQueued = showProgress && progress === 0;

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: '6px',
        padding: '0.75rem 1rem',
        marginTop: '0.75rem',
        background: 'var(--bg-2)',
      }}
    >
      <div
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: showProgress ? '0.5rem' : 0,
        }}
      >
        {(status === 'pending' || status === 'running') && (
          <span
            className="loading-spinner"
            style={{ width: '14px', height: '14px', borderWidth: '2px' }}
          />
        )}
        <span style={{ fontSize: '0.9rem', color: 'var(--text-1)' }}>{t('externalAnalysis.progressLabel', { label })}</span>
        {showProgress && (
          <span style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginLeft: '0.25rem' }}>
            {isQueued ? t('externalAnalysis.queuedHint') : `${progress}%`}
          </span>
        )}
      </div>
      {showProgress && (
        <div
          role="progressbar"
          aria-label={t('externalAnalysis.progressAriaLabel')}
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{
            height: '6px',
            width: '100%',
            background: 'var(--border)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: 'var(--primary)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ExternalAnalysisStatus;
